import { API } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import { ColumnMappingOrientation, ColumnMappingPreview, ColumnTarget, ColumnTargetKind, UpdateColumnMappingRequest } from "@swivl-mono/types";
import { Button, ButtonColor, Modal, ModalSize, SearchSelect, SelectItem, SelectOption, SelectSize, Spinner } from "@swivl-mono/ui";
import { useEffect, useMemo, useState } from "react";

import { AlertsModel } from "../../../Models/Notifications/Alerts.Model";
import { FileTile } from "./DocumentIngest.View";

/**
 * The pre-scan column-mapping modal, per the Aug-19 build spec: the operator tells us what each
 * column (or, in "Down the left" orientation, each field-row) of a tabular upload actually holds,
 * before Scan ever runs. Every change here saves immediately (the same click-to-edit-and-commit
 * pattern the rest of Document Ingest already uses) — there's no separate "draft" state to discard,
 * so Cancel/× just close; only Confirm is a distinct action (it stamps `confirmedAt`, gated on the
 * server's own freshly-recomputed warnings).
 */

interface ColumnMappingModalProps {
  orgId: number;
  uploadId: number;
  fileName: string;
  onClose: () => void;
  /** Fires after every save (and on initial load) so the queue's trigger pill stays in sync live. */
  onChange: (preview: ColumnMappingPreview | null) => void;
}

// The catalog's own kinds, grouped into the modal's spec'd sections. "Identifiers" leads because
// that's the mapping the operator actually came here to make; the finer sub-groups the spec sketches
// (Contact/Hours/Units & rates/Fees & discounts/Amenities & access) aren't a distinction the catalog
// itself carries, so facilityInfo and policyField each get one section rather than an invented split.
const CATEGORY_LABEL: Partial<Record<ColumnTargetKind, string>> = {
  identity: "Identifiers",
  facilityInfo: "Facility Info",
  schedule: "Hours",
  feature: "Amenities & Access",
  policyField: "Policy"
};
const CATEGORY_ORDER: ColumnTargetKind[] = ["identity", "facilityInfo", "schedule", "feature", "policyField"];

interface CatalogGroup {
  label: string;
  options: ColumnTarget[];
}

function groupCatalog(catalog: ColumnTarget[]): CatalogGroup[] {
  const byKind = new Map<ColumnTargetKind, ColumnTarget[]>();
  for (const target of catalog) {
    const list = byKind.get(target.kind) ?? [];
    list.push(target);
    byKind.set(target.kind, list);
  }
  return CATEGORY_ORDER.filter((kind) => byKind.has(kind)).map((kind) => ({
    label: CATEGORY_LABEL[kind] as string,
    options: (byKind.get(kind) ?? []).sort((a, b) => a.label.localeCompare(b.label))
  }));
}

// A per-option policyField target shares its `key` with the field's whole-field target and every
// sibling option — fold optionKey in too, or two different options would produce the identical
// SelectItem value, so picking one in the dropdown could silently resolve to a different option.
const targetValue = (target: ColumnTarget): string =>
  target.kind === "unmapped" ? "" : target.optionKey ? `${target.kind}:${target.key}#${target.optionKey}` : `${target.kind}:${target.key}`;
const UNMAPPED_TARGET: ColumnTarget = { kind: "unmapped", key: "", label: "" };

// Spreadsheet-style column letters (A, B, … Z, AA, AB, …).
function columnLetter(index: number): string {
  let n = index;
  let letters = "";
  do {
    letters = String.fromCharCode(65 + (n % 26)) + letters;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return letters;
}

export function ColumnMappingModal({ orgId, uploadId, fileName, onClose, onChange }: ColumnMappingModalProps): React.ReactElement {
  const [preview, setPreview] = useState<ColumnMappingPreview | null>(null);
  const [catalog, setCatalog] = useState<ColumnTarget[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [loaded, targetCatalog] = await Promise.all([
          API.DocumentIngest.getColumnMapping(orgId, uploadId),
          API.DocumentIngest.getColumnTargetCatalog(orgId)
        ]);
        if (!active) return;
        setPreview(loaded);
        onChange(loaded);
        setCatalog(targetCatalog);
      } catch (error) {
        if (active) AlertsModel.errorToast(error instanceof Error ? error.message : "Couldn’t load the column mapping, please try again.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, uploadId]);

  const groupedCatalog = useMemo(() => groupCatalog(catalog), [catalog]);

  const applyUpdate = async (req: UpdateColumnMappingRequest): Promise<ColumnMappingPreview | null> => {
    setSaving(true);
    try {
      const result = await API.DocumentIngest.updateColumnMapping(orgId, uploadId, req);
      setPreview(result);
      onChange(result);
      return result;
    } catch (error) {
      AlertsModel.errorToast(error instanceof Error ? error.message : "Couldn’t save that change, please try again.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleOrientationChange = (orientation: ColumnMappingOrientation): void => {
    void applyUpdate({ orientation, confirm: false });
  };

  const handleFieldTargetChange = (index: number, target: ColumnTarget): void => {
    void applyUpdate({ fields: [{ index, target }], confirm: false });
  };

  const handleConfirm = async (): Promise<void> => {
    const result = await applyUpdate({ confirm: true });
    if (result?.mapping.confirmedAt) onClose();
  };

  const startEdit = (key: string, initial: string): void => {
    setEditingKey(key);
    setDraft(initial);
  };
  const commitEdit = (effectiveRow: number, effectiveCol: number): void => {
    setEditingKey(null);
    if (!preview) return;
    const current = preview.sampleRows[effectiveRow]?.[effectiveCol] ?? "";
    if (draft === current) return;
    void applyUpdate({ cellCorrections: { [`${effectiveRow}:${effectiveCol}`]: draft }, confirm: false });
  };

  if (loading || !preview) {
    return (
      <Modal isOpen onClose={() => onClose()} title={fileName} size={ModalSize.LARGE}>
        <div className="flex items-center justify-center gap-2 px-8 py-16 text-grey-500">
          {loading ? (
            <>
              <Spinner /> <span className="text-sm">Loading…</span>
            </>
          ) : (
            <span className="text-sm">Couldn’t load this file’s columns.</span>
          )}
        </div>
      </Modal>
    );
  }

  const { mapping } = preview;
  const orientation = mapping.orientationConfirmed ?? mapping.orientation;
  const isRowMode = orientation === "rows";

  return (
    <Modal
      isOpen
      onClose={() => onClose()}
      hasTitleBorder
      className="flex h-[min(88vh,820px)] w-[1180px] max-w-[calc(100vw-64px)] flex-col overflow-hidden !p-0"
      title={
        <div className="flex w-full items-start justify-between gap-6">
          <div className="flex min-w-0 items-center gap-3.5">
            <FileTile fileName={fileName} size={28} />
            <div className="min-w-0">
              <div className="truncate font-poppins text-[20px] font-semibold text-grey-900">{fileName}</div>
            </div>
          </div>
          <div className="mt-0.5 flex shrink-0 items-center gap-2">
            <span className="text-[12px] font-semibold text-grey-500">Labels run</span>
            <div className="flex overflow-hidden rounded-[4px] border border-grey-300">
              <button
                type="button"
                disabled={saving}
                onClick={() => handleOrientationChange("columns")}
                className={filterClasses(
                  "px-3 py-[5px] text-[13px] font-semibold whitespace-nowrap transition-colors",
                  !isRowMode ? "bg-grey-900 text-white" : "bg-white text-grey-700 hover:bg-grey-50"
                )}
              >
                Across the top
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => handleOrientationChange("rows")}
                className={filterClasses(
                  "px-3 py-[5px] text-[13px] font-semibold whitespace-nowrap transition-colors",
                  isRowMode ? "bg-grey-900 text-white" : "bg-white text-grey-700 hover:bg-grey-50"
                )}
              >
                Down the left
              </button>
            </div>
          </div>
        </div>
      }
    >
      {preview.warnings.length > 0 && (
        <div className="flex shrink-0 flex-col gap-1.5 border-b border-grey-200 bg-orange-50 px-7 py-3">
          {preview.warnings.map((warning) => (
            <p key={warning.message} className="text-[13px] text-orange-700">
              {warning.message}
            </p>
          ))}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto bg-grey-50">
        {isRowMode ? (
          <RowModeGrid
            preview={preview}
            groupedCatalog={groupedCatalog}
            saving={saving}
            editingKey={editingKey}
            draft={draft}
            onStartEdit={startEdit}
            onDraftChange={setDraft}
            onCommit={commitEdit}
            onCancelEdit={() => setEditingKey(null)}
            onFieldTargetChange={handleFieldTargetChange}
          />
        ) : (
          <ColumnModeGrid
            preview={preview}
            groupedCatalog={groupedCatalog}
            saving={saving}
            editingKey={editingKey}
            draft={draft}
            onStartEdit={startEdit}
            onDraftChange={setDraft}
            onCommit={commitEdit}
            onCancelEdit={() => setEditingKey(null)}
            onFieldTargetChange={handleFieldTargetChange}
          />
        )}
      </div>

      <div className="flex shrink-0 items-center justify-end gap-4 border-t border-grey-200 bg-grey-50 px-7 py-4">
        <div className="flex items-center gap-4">
          <Button color={ButtonColor.WHITE} onClick={() => onClose()} disabled={saving}>
            Cancel
          </Button>
          <Button color={ButtonColor.ORANGE} onClick={() => void handleConfirm()} disabled={saving || preview.warnings.length > 0} loading={saving}>
            Confirm
          </Button>
        </div>
      </div>
    </Modal>
  );
}

interface FieldMappingSelectProps {
  value: ColumnTarget;
  groupedCatalog: CatalogGroup[];
  disabled: boolean;
  onChange: (target: ColumnTarget) => void;
}

const IGNORE_COLUMN_OPTION: SelectOption = { label: "Don't map this column", value: "" };

// Searchable — the catalog runs to 150+ targets across policy/amenity/facility fields, so scanning
// a plain dropdown for one isn't realistic. Reuses the same SearchSelect the location-reassign
// picker already uses (BulkImportPage.tsx) rather than hand-rolling a combobox. "Don't map this
// column" sits ungrouped at the top; every catalog target follows, grouped exactly as before.
function FieldMappingSelect({ value, groupedCatalog, disabled, onChange }: FieldMappingSelectProps): React.ReactElement {
  const byValue = useMemo(() => {
    const map = new Map<string, ColumnTarget>();
    for (const group of groupedCatalog) for (const target of group.options) map.set(targetValue(target), target);
    return map;
  }, [groupedCatalog]);

  const items: SelectItem[] = useMemo(
    () => [
      IGNORE_COLUMN_OPTION,
      ...groupedCatalog.map((group) => ({
        type: "group" as const,
        label: group.label,
        options: group.options.map((target) => ({ label: target.label, value: targetValue(target) }))
      }))
    ],
    [groupedCatalog]
  );

  return (
    <SearchSelect
      options={items}
      selectedValues={[targetValue(value)]}
      onChange={(values) => onChange(values[0] ? (byValue.get(values[0]) ?? UNMAPPED_TARGET) : UNMAPPED_TARGET)}
      disabled={disabled}
      size={SelectSize.SMALL}
      hasSearchInDropdown
      placeholder="Don't map this column"
    />
  );
}

interface GridProps {
  preview: ColumnMappingPreview;
  groupedCatalog: CatalogGroup[];
  saving: boolean;
  editingKey: string | null;
  draft: string;
  onStartEdit: (key: string, initial: string) => void;
  onDraftChange: (value: string) => void;
  onCommit: (effectiveRow: number, effectiveCol: number) => void;
  onCancelEdit: () => void;
  onFieldTargetChange: (index: number, target: ColumnTarget) => void;
}

const CELL_INPUT_CLASS = "h-[34px] w-full border-2 border-orange-500 bg-orange-50 px-2.5 text-[13px] outline-none";

function CellEditor({
  value,
  draft,
  saving,
  isEditing,
  onStart,
  onDraftChange,
  onCommit,
  onCancelEdit,
  corrected,
  dimmed
}: {
  value: string;
  draft: string;
  saving: boolean;
  isEditing: boolean;
  onStart: () => void;
  onDraftChange: (value: string) => void;
  onCommit: () => void;
  onCancelEdit: () => void;
  corrected: boolean;
  dimmed: boolean;
}): React.ReactElement {
  if (isEditing) {
    return (
      <input
        autoFocus
        value={draft}
        disabled={saving}
        onChange={(event) => onDraftChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur();
          else if (event.key === "Escape") {
            onCancelEdit();
            event.currentTarget.blur();
          }
        }}
        onBlur={onCommit}
        className={CELL_INPUT_CLASS}
      />
    );
  }
  return (
    <div
      onClick={onStart}
      title="Click to edit"
      className={filterClasses(
        "flex h-[34px] cursor-pointer items-center truncate px-2.5 text-[13px]",
        corrected ? "text-grey-900 shadow-[inset_2px_0_0_var(--color-orange-500)]" : dimmed ? "text-grey-400" : "text-grey-700"
      )}
    >
      {value}
    </div>
  );
}

// "Across the top": one sticky <th> per column (letter + fill rate + select, as two stacked divs —
// never two separate sticky rows, or a hard-coded second offset drifts from the rendered height),
// then the file's own row 1 as an ordinary data row, then the real data rows.
function ColumnModeGrid({
  preview,
  groupedCatalog,
  saving,
  editingKey,
  draft,
  onStartEdit,
  onDraftChange,
  onCommit,
  onCancelEdit,
  onFieldTargetChange
}: GridProps): React.ReactElement {
  const { mapping, sampleRows } = preview;
  const columnWidth = 190;
  const gutterWidth = 44;

  return (
    <table className="table-fixed border-separate border-spacing-0" style={{ width: gutterWidth + mapping.fields.length * columnWidth }}>
      <colgroup>
        <col style={{ width: gutterWidth }} />
        {mapping.fields.map((field) => (
          <col key={field.index} style={{ width: columnWidth }} />
        ))}
      </colgroup>
      <thead>
        <tr>
          <th className="sticky top-0 left-0 z-30 border-b border-grey-300 bg-grey-100" />
          {mapping.fields.map((field) => (
            <th
              key={field.index}
              className={filterClasses(
                "sticky top-0 z-20 border-b border-l border-grey-300 p-2 text-left align-top",
                field.state === "duplicate" ? "bg-orange-50" : "bg-grey-100"
              )}
            >
              <div className="flex flex-col gap-[5px]">
                <div className="flex items-center justify-between">
                  <span className={filterClasses("text-[10px] font-bold", field.state === "ignored" ? "text-grey-400" : "text-orange-700")}>
                    {columnLetter(field.index)}
                  </span>
                  <span className="text-[10px] text-grey-500 tabular-nums">{Math.round(field.fillRate * 100)}%</span>
                </div>
                <FieldMappingSelect
                  value={field.target}
                  groupedCatalog={groupedCatalog}
                  disabled={saving}
                  onChange={(target) => onFieldTargetChange(field.index, target)}
                />
                {field.state === "duplicate" && <span className="text-[11px] text-orange-700">Already used by another column</span>}
                {field.state === "ignored" && (
                  <span className="text-[11px] leading-[15px] text-grey-500">Not recognized - pick a field or leave it to AI</span>
                )}
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className="sticky left-0 z-10 border-b border-grey-200 bg-grey-100 text-center text-[11px] text-grey-500 tabular-nums">1</td>
          {mapping.fields.map((field) => (
            <td
              key={field.index}
              className={filterClasses(
                "h-[34px] truncate border-b border-grey-200 px-2.5 text-[13px] font-semibold",
                field.state === "ignored" ? "bg-grey-50 text-grey-400" : "bg-white text-grey-900"
              )}
            >
              {field.label}
            </td>
          ))}
        </tr>
        {sampleRows.map((row, rowIndex) => (
          <tr key={rowIndex}>
            <td className="sticky left-0 z-10 border-b border-grey-100 bg-grey-100 text-center text-[11px] text-grey-500 tabular-nums">
              {rowIndex + 2}
            </td>
            {mapping.fields.map((field) => {
              const key = `${rowIndex}:${field.index}`;
              return (
                <td
                  key={field.index}
                  className={filterClasses("h-[34px] border-b border-grey-100 p-0", field.state === "ignored" ? "bg-grey-50" : "bg-white")}
                >
                  <CellEditor
                    value={row[field.index] ?? ""}
                    draft={draft}
                    saving={saving}
                    isEditing={editingKey === key}
                    onStart={() => onStartEdit(key, row[field.index] ?? "")}
                    onDraftChange={onDraftChange}
                    onCommit={() => onCommit(rowIndex, field.index)}
                    onCancelEdit={onCancelEdit}
                    corrected={!!mapping.cellCorrections?.[key]}
                    dimmed={field.state === "ignored"}
                  />
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// "Down the left": 3 frozen left columns (row-number gutter, "Label in file", "Use as"), then one
// sticky column per location. `sampleRows` is already normalized to [locationIndex][fieldIndex] (the
// same shape a "columns"-mode sheet would have — see transposeSheet), so a field row's per-location
// values are a COLUMN read of `sampleRows`, not a row read.
function RowModeGrid({
  preview,
  groupedCatalog,
  saving,
  editingKey,
  draft,
  onStartEdit,
  onDraftChange,
  onCommit,
  onCancelEdit,
  onFieldTargetChange
}: GridProps): React.ReactElement {
  const { mapping, sampleRows } = preview;
  const locationLabels = mapping.locationLabels ?? [];
  const gutterWidth = 44;
  const labelWidth = 210;
  const useAsWidth = 200;
  const locationColumnWidth = 190;
  const labelLeft = gutterWidth;
  const useAsLeft = gutterWidth + labelWidth;

  return (
    <table
      className="table-fixed border-separate border-spacing-0"
      style={{ width: gutterWidth + labelWidth + useAsWidth + locationLabels.length * locationColumnWidth }}
    >
      <colgroup>
        <col style={{ width: gutterWidth }} />
        <col style={{ width: labelWidth }} />
        <col style={{ width: useAsWidth }} />
        {locationLabels.map((_, index) => (
          <col key={index} style={{ width: locationColumnWidth }} />
        ))}
      </colgroup>
      <thead>
        <tr>
          <th className="sticky top-0 left-0 z-40 border-b border-grey-300 bg-grey-100" />
          <th
            className="sticky top-0 z-40 border-b border-grey-300 bg-grey-100 p-2 text-left text-[11px] font-bold tracking-[0.06em] text-grey-900 uppercase"
            style={{ left: labelLeft }}
          >
            Label in file
          </th>
          <th
            className="sticky top-0 z-40 border-b border-grey-300 bg-grey-100 p-2 text-left text-[11px] font-bold tracking-[0.06em] text-grey-900 uppercase"
            style={{ left: useAsLeft }}
          >
            Use as
          </th>
          {locationLabels.map((label, index) => (
            <th key={index} className="sticky top-0 z-20 border-b border-l border-grey-300 bg-grey-100 p-2 text-left">
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-bold text-orange-700">{columnLetter(index + 1)}</span>
                <span className="truncate text-[11px] font-bold text-grey-900" title={label}>
                  {label}
                </span>
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {mapping.fields.map((field) => (
          <tr key={field.index}>
            <td className="sticky left-0 z-30 border-b border-grey-200 bg-grey-100 text-center text-[11px] text-grey-500 tabular-nums">
              {field.index + 1}
            </td>
            <td className="sticky z-30 border-b border-grey-200 bg-white p-2 align-top" style={{ left: labelLeft }}>
              <div className={filterClasses("truncate text-[13px] font-semibold", field.state === "ignored" ? "text-grey-400" : "text-grey-900")}>
                {field.label}
              </div>
              <div className="text-[10px] text-grey-500 tabular-nums">{Math.round(field.fillRate * 100)}% filled</div>
            </td>
            <td className="sticky z-30 border-b border-grey-200 bg-white p-2 align-top" style={{ left: useAsLeft }}>
              <FieldMappingSelect
                value={field.target}
                groupedCatalog={groupedCatalog}
                disabled={saving}
                onChange={(target) => onFieldTargetChange(field.index, target)}
              />
              {field.state === "duplicate" && <span className="mt-1 block text-[11px] text-orange-700">Already used by another row</span>}
            </td>
            {locationLabels.map((_, locationIndex) => {
              const key = `${locationIndex}:${field.index}`;
              const value = sampleRows[locationIndex]?.[field.index] ?? "";
              return (
                <td
                  key={locationIndex}
                  className={filterClasses("h-[34px] border-b border-l border-grey-100 p-0", field.state === "ignored" ? "bg-grey-50" : "bg-white")}
                >
                  <CellEditor
                    value={value}
                    draft={draft}
                    saving={saving}
                    isEditing={editingKey === key}
                    onStart={() => onStartEdit(key, value)}
                    onDraftChange={onDraftChange}
                    onCommit={() => onCommit(locationIndex, field.index)}
                    onCancelEdit={onCancelEdit}
                    corrected={!!mapping.cellCorrections?.[key]}
                    dimmed={field.state === "ignored"}
                  />
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
